import { EventEmitter, h, Component, Host, State, Event, Element, Prop } from '@stencil/core';

import type { TableStateInterface } from '../../../../services/interfaces';
import { cssClassListOneOf, getCSSVariable } from '../../../../services/utils';
import { getTableState } from '../helpers/table-state';

@Component({
  tag: 'pk-table-row',
  styleUrl: './scss/pk-table-row.scss',
  scoped: true,
})
export class PkTableRow {
  // Own Properties
  // ====================

  private tableState: TableStateInterface;
  private isColumnHeader: boolean;
  private checkboxElement: HTMLPkCheckboxElement;

  // Reference to host HTML element
  // ===================================
  @Element() el!: HTMLPkTableRowElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private _isChecked: boolean;

  // Public Property API
  // ========================

  /** Will specify whether if the table row has been selected or not  */
  @Prop({ reflect: true }) selected: boolean;

  /** Changes the row background color based on provided token. */
  @Prop({ reflect: true }) bgColor?: string = 'elevation-01';

  /** Changes the row cells color based on provided token. */
  @Prop({ reflect: true }) cellColor?: string = 'text-03';

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called when the table row is clicked */
  @Event() pkTableRowClick: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    const tableId = this.el.closest<HTMLPkTableElement>('pk-table[table-id]')?.tableId;
    this.tableState = getTableState(tableId);
    this.isColumnHeader = this.el.parentElement.tagName === 'PK-TABLE-HEADER' || !!this.el.closest('pk-table-header');
  }

  componentDidLoad() {
    this.attachEvenListeners();
  }

  disconnectedCallback() {
    this.clearEventListeners();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // ==========================

  private attachEvenListeners = () => {
    this.getCheckboxElement();
    this.el.addEventListener('click', this.handleRowClick);
  };

  private clearEventListeners = () => {
    this.el.removeEventListener('click', this.handleRowClick);
    if (this.checkboxElement) {
      this.checkboxElement.removeEventListener('pkCheckboxChange', this.handleCheckboxAction);
    }
  };

  private getCheckboxElement = () => {
    if (!this.el.children.length) return;

    this.checkboxElement = this.el.children[0].querySelector('pk-checkbox');
    if (this.checkboxElement) {
      this._isChecked = this.checkboxElement.checked;
      this.checkboxElement.addEventListener('pkCheckboxChange', this.handleCheckboxAction);
    }
  };

  private handleCheckboxAction = (event: CustomEvent<{ isChecked: boolean }>) => {
    this._isChecked = event.detail.isChecked;

    if (!this._isChecked) {
      this.selected = false;
    }
  };

  private handleRowClick = (ev?: UIEvent) => {
    if (this.isColumnHeader) return;

    if (!this.tableState) return;

    const { disableClick } = this.tableState;
    if (disableClick) return;

    const elemTarget = ev.target as HTMLTableRowElement;
    const isActionIcon =
      cssClassListOneOf(elemTarget, ['pk-icon--ripple']) && elemTarget.classList.contains('is-hover');
    const isRadioCheckbox = cssClassListOneOf(elemTarget, [
      'pk-checkbox--icon-ripple',
      'pk-checkbox--icon-input',
      'pk-radiobutton--icon-input',
      'pk-radiobutton--icon-ripple',
    ]);
    if (isRadioCheckbox || isActionIcon) return;
    if (elemTarget.tagName.toLowerCase() === 'pk-inline-edit') return;

    this.selected = !this.selected;
    this.pkTableRowClick.emit(ev);
  };

  private get rowStyle() {
    if (!this.tableState) return {};

    const { rowSpacing, rowRounded } = this.tableState;

    const result = {};

    if (rowRounded) {
      result['--row--border-radius'] = getCSSVariable(`border-radius-${rowRounded}`);
    }

    if (rowSpacing !== 0 && (rowSpacing < 8 || rowSpacing % 8 !== 0)) {
      console.warn('Cell padding minimum is 0, and must be a multiple of 8.');
      return result;
    }
    if (!rowSpacing && rowSpacing !== 0) {
      result['--row--spacing'] = null;
      return result;
    }

    if (rowSpacing === 0) {
      result['--row--spacing'] = '0';
      result['--row--border-radius'] = '0';
      return result;
    }

    result['--row--spacing'] = `${rowSpacing.toString()}px`;
    return result;
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    if (!this.tableState) return null;

    const { useFixedWidth, firstCellMobileHeader, rowSpacing, rowShadow, rowStroke, rowDivider } = this.tableState;

    const style = {
      ...(this.bgColor && { '--row--bg-color': getCSSVariable(this.bgColor) }),
      ...(this.cellColor && { '--row--color': getCSSVariable(this.cellColor) }),
      ...this.rowStyle,
    };

    return (
      <Host
        role="row"
        class={{
          'pk-table--row': true,
          'first-cell-mobile-header': firstCellMobileHeader,
          'is-checked': !this.isColumnHeader && this._isChecked,
          'is-focused': !this.isColumnHeader && this.selected,
          'fixed-width': useFixedWidth,
          'has-shadow': !this.isColumnHeader && rowSpacing && rowShadow,
          'has-stroke': !this.isColumnHeader && rowSpacing && rowStroke,
          'has-divider': !this.isColumnHeader && !rowSpacing && rowDivider,
        }}
        style={{ ...style }}
      >
        <slot />
      </Host>
    );
  }
}
